import React, { useState } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Bluebird from 'bluebird';
import { Button, Card, CardContent, CardActions } from '@mui/material';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import * as authService from '../services/auth';
import * as usersActions from '../actions/users';
import * as uiPersistedActions from '../actions/uiPersisted';
import ButtonWithLoading from './ButtonWithLoading';

const UnverifiedEmailNotification = props => {
  const { emailVerificationNotificationVisible } = props;
  const currentlyLoggedInOrImpersonatingUser = authService.getCurrentlyLoggedInOrImpersonatingUser();
  const isDrawerOpen = useSelector((state) => state.ui.persisted.isDrawerOpen);
  const [isLoading, setLoading] = useState(false);

  const onResendEmailClick = () => {
    const { site, resendEmail } = props;
    const user = authService.getCurrentlyLoggedInOrImpersonatingUser();

    setLoading(true);

    return Bluebird
      .try(() => resendEmail(user.id, site ? site.id : null))
      .then(() => snackbarHelper.success(`Verification email sent to ${user.email}`))
      .catch(err => errorsHelper.handleError(err))
      .finally(() => setLoading(false));
  };

  const onDismiss = () => {
    const { dismissEmailVerificationNotification } = props;

    dismissEmailVerificationNotification();
  };

  if (
    authService.isCompleteRegistrationProcessPending()
    || currentlyLoggedInOrImpersonatingUser.email_verified !== null
    || !emailVerificationNotificationVisible
  ) {
    return null;
  }

  const paddingLeftValueAboveSmBreakpoint = (() => {
    if (
      currentlyLoggedInOrImpersonatingUser &&
      !authService.shouldHideMainDrawer(currentlyLoggedInOrImpersonatingUser)
    ) {
      return isDrawerOpen ? '240px' : '75px';
    }

    return '16px';
  })();

  return (
    <Card 
      sx={{
        backgroundColor: '#e1ebfd',
        width: '100%',
        paddingLeft: { xs: 0, sm: paddingLeftValueAboveSmBreakpoint },
      }}
    >
      <CardContent style={{ paddingBottom: 0 }} data-testid="unverified-email-card">
        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>Unverified email address</div>

        <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.54)' }}>
          You can't claim rescues until you have verified your email address. Press RESEND EMAIL to resend the
          verification email to <strong>{currentlyLoggedInOrImpersonatingUser.email}</strong>. Or, visit Manage
          Profile to change your email address.
        </div>
      </CardContent>

      <CardActions>
        <ButtonWithLoading
          size="small"
          color="primary"
          variant="contained"
          onClick={onResendEmailClick}
          data-testid="resend-email-button"
          isLoading={isLoading}
        >
          Resend email
        </ButtonWithLoading>

        <Button size="small" color="primary" onClick={onDismiss}>
          Do this later
        </Button>
      </CardActions>
    </Card>
  );
};

const mapStateToProps = ({
  ui: { persisted: { emailVerificationNotificationVisible } },
  app: { site, loggedInUser, impersonating }
}) => ({
  site,
  emailVerificationNotificationVisible,
  loggedInUser,
  impersonating,
});
const mapDispatchToProps = dispatch => ({
  resendEmail: (userId, siteId = null) => dispatch(usersActions.resendUserVerificationEmail(userId, siteId)),
  dismissEmailVerificationNotification: () => dispatch(uiPersistedActions.dismissEmailVerificationNotification()),
  showEmailVerificationNotification: () => dispatch(uiPersistedActions.showEmailVerificationNotification()),
});

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(UnverifiedEmailNotification);
